import IResult from "../../models/result/IResult";
import SuccessResult from "../../models/result/SuccessResult";
import InvalidResult from "../../models/result/InvalidResult";
import UnauthorizedResult from "../../models/result/UnauthorizedResult";
import UnexpectedResult from "../../models/result/UnexpectedResult";
import { getAnalyticsWebsocketApiUrl, getBaseApiUrl } from "../../constants/Urls";
import { VOICIFY_ACCESS_TOKEN_STORE, VOICIFY_API_KEY } from "../../constants/Keys";
import NotFoundResult from "../../models/result/NotFoundResult";
import PermissionDeniedResult from "../../models/result/PermissionDeniedResult";

// generic http method for interacting with the voicify api
export const sendPost = async <T>(url: string, body: any, baseUrl?: string, tokenOverride: string = null): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders(tokenOverride);
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: JSON.stringify(body)
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in sendPost:', error);
        return new UnexpectedResult();
    }
};

export const sendPostWithCustomHeaders = async <T>(url: string, body: any, customHeaders: Record<string, string>, baseUrl?: string): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...customHeaders
            },
            body: JSON.stringify(body)
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in sendPostWithCustomHeaders:', error);
        return new UnexpectedResult();
    }
};

export const sendPutWithCustomHeaders = async <T>(url: string, body: any, customHeaders: Record<string, string>, baseUrl?: string): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const response = await fetch(fullUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...customHeaders
            },
            body: JSON.stringify(body)
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in sendPutWithCustomHeaders:', error);
        return new UnexpectedResult();
    }
};

export const sendPostForFile = async (url: string, body: any, baseUrl?: string): Promise<IResult<Blob>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: JSON.stringify(body)
        });
        return await handleFetchResponseBlob(response);
    }
    catch (error) {
        console.error('Network error in sendPostForFile:', error);
        return new UnexpectedResult();
    }
};

export const uploadFile = async <T>(url: string, file: File, baseUrl?: string): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const formData = new FormData();
        formData.append("file", file);
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: headers,
            body: formData
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in uploadFile:', error);
        return new UnexpectedResult();
    }
};

export const putFile = async <T>(url: string, file: File, progressCallback?: (number) => void): Promise<IResult<T>> => {
    try {
        return new Promise<IResult<T>>((resolve) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && progressCallback) {
                    const percentCompleted = Math.floor((event.loaded * 100) / event.total);
                    progressCallback(percentCompleted);
                }
            };

            xhr.onload = async () => {
                const mockResponse = {
                    status: xhr.status,
                    text: () => Promise.resolve(xhr.responseText),
                    json: () => Promise.resolve(xhr.responseText ? JSON.parse(xhr.responseText) : null),
                    blob: () => Promise.resolve(new Blob([xhr.response]))
                } as Response;

                const result = await handleFetchResponse<T>(mockResponse);
                resolve(result);
            };

            xhr.onerror = () => {
                console.error('Network error in putFile:', xhr.status, xhr.statusText);
                resolve(new UnexpectedResult());
            };

            xhr.ontimeout = () => {
                console.error('Timeout error in putFile');
                resolve(new UnexpectedResult());
            };

            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', getFileType(file.type));

            // Set timeout to prevent hanging requests
            xhr.timeout = 300000; // 5 minutes

            xhr.send(file);
        });
    }
    catch (error) {
        console.error('Error in putFile:', error);
        return new UnexpectedResult();
    }
};


const getFileType = (fileType: string) => {
    // override file type for content type since certain browsers just make up their own rules
    if (fileType == "audio/mpeg") return "audio/mp3";
    return fileType;
}


export const sendGet = async <T>(url: string, baseUrl?: string, signal?: AbortSignal): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const options: RequestInit = {
            method: 'GET',
            headers: headers
        };
        if (signal !== undefined) {
            options.signal = signal;
        }
        const response = await fetch(fullUrl, options);
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        if (error && error.name === 'AbortError') {
            return new InvalidResult<T>("AbortError");
        }
        console.error('Network error in sendGet:', error);
        return new UnexpectedResult();
    }
};

export const connectWebsocket = async <T>(url: string, baseUrl?: string): Promise<WebSocket> => {
    const fullUrl = (baseUrl === undefined ? getAnalyticsWebsocketApiUrl() : baseUrl) + url;
    try {
        let accessToken = sessionStorage.getItem(VOICIFY_ACCESS_TOKEN_STORE);
        const ws = new WebSocket(fullUrl, ["voicify-client", `bearer-${accessToken}`]);
        return ws;
    }
    catch (error) {
        return null;
    }
};


export const sendGetFile = async (url: string, baseUrl?: string): Promise<IResult<Blob>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: headers
        });
        return await handleFetchResponseBlob(response);
    }
    catch (error) {
        console.error('Network error in sendGetFile:', error);
        return new UnexpectedResult();
    }
};

export const sendPut = async <T>(url: string, body?: any, baseUrl?: string): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const response = await fetch(fullUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: body ? JSON.stringify(body) : undefined
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in sendPut:', error);
        return new UnexpectedResult();
    }
};

export const sendDelete = async <T>(url: string, baseUrl?: string): Promise<IResult<T>> => {
    const fullUrl = (baseUrl === undefined ? getBaseApiUrl() : baseUrl) + url;
    try {
        const headers = getHeaders();
        const response = await fetch(fullUrl, {
            method: 'DELETE',
            headers: headers
        });
        return await handleFetchResponse<T>(response);
    }
    catch (error) {
        console.error('Network error in sendDelete:', error);
        return new UnexpectedResult();
    }
};

export const queryStringSerialize = (model: any): string => {
    const stringArray = [];
    for (const property in model) {
        if (model.hasOwnProperty(property) && model[property] != null) {
            stringArray.push(encodeURIComponent(property) + "=" + encodeURIComponent(model[property]));
        }
    }

    return stringArray.join("&");
}

const getHeaders = (tokenOverride = null): Record<string, string> => {
    const headers: Record<string, string> = {
        'Access-Control-Allow-Origin': '*'
    };

    // pull token from local store
    let accessToken = sessionStorage.getItem(VOICIFY_ACCESS_TOKEN_STORE);
    if (accessToken?.length || tokenOverride?.length) {
        if (tokenOverride?.length) {
            accessToken = tokenOverride;
        }
        // set authorization header
        if (accessToken !== undefined) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }
    } else {
        let apiKey = sessionStorage.getItem(VOICIFY_API_KEY);
        if (apiKey?.length) {
            // set api key header
            headers['X-Api-Key'] = apiKey;
        }
    }

    return headers;
}

// maps a fetch response to a result
const handleFetchResponse = async <T>(response: Response): Promise<IResult<T>> => {
    if (!response)
        return new UnexpectedResult();

    if (response.status === 200 || response.status === 204) {
        try {
            // Handle empty responses
            if (response.status === 204) {
                return new SuccessResult(null);
            }

            const text = await response.text();
            if (!text) {
                return new SuccessResult(null);
            }

            const contentType = response.headers.get('content-type') || '';

            // If content-type indicates plain text, return as-is
            if (contentType.includes('text/plain')) {
                return new SuccessResult(text as unknown as T);
            }

            // Try to parse as JSON (handles objects, arrays, and primitives like booleans, numbers)
            const trimmedText = text.trim();

            // Check if it looks like JSON (objects, arrays, or primitive JSON values)
            const looksLikeJson =
                trimmedText.startsWith('{') ||
                trimmedText.startsWith('[') ||
                trimmedText === 'true' ||
                trimmedText === 'false' ||
                trimmedText === 'null' ||
                !isNaN(Number(trimmedText));

            if (looksLikeJson) {
                try {
                    const data = JSON.parse(text);
                    return new SuccessResult(data as T);
                } catch {
                    // If JSON parsing fails, return as text
                    return new SuccessResult(text as unknown as T);
                }
            }

            // Not JSON, return as text
            return new SuccessResult(text as unknown as T);
        } catch (error) {
            console.error('Error parsing response:', error);
            console.error('Response URL:', response.url);
            console.error('Response status:', response.status);
            console.error('Response headers:', Object.fromEntries(response.headers.entries()));
            return new UnexpectedResult();
        }
    }
    else if (response.status === 400) {
        try {
            const data = await response.json();
            const errors = data as string[];
            let error = errors[0];
            if (error == undefined) {
                const attributeErrorNames = Object.getOwnPropertyNames(errors)
                if (attributeErrorNames[0] != undefined && errors[attributeErrorNames[0]] != null) {
                    error = errors[attributeErrorNames[0]];
                }
                else {
                    error = data["Responses[0].Content"][0]
                }
            }
            return new InvalidResult(error);
        } catch (error) {
            return new InvalidResult('Bad Request');
        }
    }
    else if (response.status === 401) {
        return new UnauthorizedResult();
    }
    else if (response.status === 404) {
        return new NotFoundResult();
    }
    else if (response.status === 403) {
        return new PermissionDeniedResult(await getResponseError(response));
    }

    return new UnexpectedResult();
}

// maps a fetch response to a blob result
const handleFetchResponseBlob = async (response: Response): Promise<IResult<Blob>> => {
    if (!response)
        return new UnexpectedResult();

    if (response.status === 200 || response.status === 204) {
        try {
            const blob = await response.blob();
            return new SuccessResult(blob);
        } catch (error) {
            console.error('Error parsing blob response:', error);
            return new UnexpectedResult();
        }
    }
    else if (response.status === 400) {
        try {
            const data = await response.json();
            const errors = data as string[];
            let error = errors[0];
            if (error == undefined) {
                const attributeErrorNames = Object.getOwnPropertyNames(errors)
                if (attributeErrorNames[0] != undefined && errors[attributeErrorNames[0]] != null) {
                    error = errors[attributeErrorNames[0]];
                }
                else {
                    error = data["Responses[0].Content"][0]
                }
            }
            return new InvalidResult(error);
        } catch (error) {
            return new InvalidResult('Bad Request');
        }
    }
    else if (response.status === 404) {
        return new NotFoundResult();
    }
    else if (response.status === 403) {
        return new PermissionDeniedResult(await getResponseError(response));
    }

    return new UnexpectedResult();
}

const getResponseError = async (response: Response): Promise<string> => {
    const fallback = "You do not have permission to perform this action.";
    try {
        const responseText = await response.text();
        if (!responseText || !responseText.trim()) {
            return fallback;
        }

        let data: any;
        try {
            data = JSON.parse(responseText);
        } catch {
            return responseText.trim();
        }

        if (Array.isArray(data) && data.length > 0) {
            return data[0];
        }
        if (data && Array.isArray(data.errors) && data.errors.length > 0) {
            return data.errors[0];
        }
        if (typeof data === "string") {
            return data;
        }
    } catch {
        return fallback;
    }
    return fallback;
}
